import { useEffect, useRef, useState } from "react";
import QueryModal from "views/components/QueryModal";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import styles from "./style.module.scss";
import useVqlParams from "hooks/useVqlParams";
import { Filters } from "types";
import { useDispatch } from "react-redux";
import {
  updateSelectedQueryModal,
  setSelectedQueryModal,
} from "redux/SingleDataset/actions";
import VLCropper, { VLCropperRef } from "views/uikit/VLCropper";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { CropIcon, VisualSimilarityModalIcon } from "../SearchContainer/Icons";
import { createAndUpdateVisualSimilarityFilter } from "helpers/utility/vqlHelpers";
import { useHotkeys } from "react-hotkeys-hook";

interface VisualSimilarityQueryModalProps {
  editQueryId?: string;
  image?: {
    uri: string;
    boundingBox?: [number, number, number, number];
  };
}

export default function VisualSimilarityQueryModal({
  editQueryId,
  image,
}: VisualSimilarityQueryModalProps) {
  const { findVqlQuery, setVqlQueries, vqlQueries } = useVqlParams();
  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "similarity") : null;
  const queryId = oldQuery?.id;
  const modalRef = useRef<HTMLDivElement>(null);
  const cropperRef = useRef<VLCropperRef>(null);
  const dispatch = useDispatch();
  const [hasUserModifiedCrop, setHasUserModifiedCrop] = useState(false);
  const [autoCrop, setAutoCrop] = useState(false);
  const [hasUserResetCrop, setHasUserResetCrop] = useState(false);
  const [currentCropData, setCurrentCropData] = useState<
    [number, number, number, number] | null | undefined
  >(undefined);

  const handleCropChange = (
    modified: boolean,
    cropData?: [number, number, number, number] | null,
  ) => {
    setHasUserModifiedCrop(modified);
    // Reset the "user reset crop" state when user starts cropping again
    if (modified && hasUserResetCrop) {
      setHasUserResetCrop(false);
    }
    if (cropData !== undefined) {
      setCurrentCropData(cropData);
    }
  };

  const handleApply = () => {
    if (!image) return;

    // Determine bounding box to use
    let boundingBoxToUse: [number, number, number, number] | undefined =
      undefined;
    if (currentCropData !== undefined) {
      // currentCropData has been set by user interaction
      // If it's null, it means full image (no bounding box)
      // If it has coordinates, use them
      boundingBoxToUse = currentCropData || undefined;
    } else if (image.boundingBox) {
      // Fall back to original bounding box if no crop data set yet
      boundingBoxToUse = image.boundingBox;
    }

    // Get the reference image ID and operation type
    const existingQuery =
      oldQuery ||
      vqlQueries.find((q) => "similarity" in q && q?.id === editQueryId);

    const referenceImageId = (existingQuery as any)?.similarity?.value || "";
    const operation =
      existingQuery &&
      "similarity" in existingQuery &&
      existingQuery.similarity?.op
        ? (existingQuery.similarity.op as "media" | "upload")
        : "media";

    // Create the visual similarity query using the helper
    const newQuery = createAndUpdateVisualSimilarityFilter(
      referenceImageId,
      boundingBoxToUse,
      editQueryId || undefined,
      operation,
      undefined, // negation - let function preserve existing
      existingQuery || oldQuery || undefined, // pass existing query to preserve properties
    );

    // Add or update the VQL query
    const updatedQueries =
      editQueryId && oldQuery
        ? vqlQueries.map((q) => (q.id === editQueryId ? newQuery : q))
        : [...vqlQueries, newQuery];

    setVqlQueries(updatedQueries);

    // Close the modal
    dispatch(setSelectedQueryModal(null));
  };

  useHotkeys("Enter", () => {
    if (hasUserModifiedCrop) {
      handleApply();
    }
  });

  const handleResetCrop = () => {
    setAutoCrop(false);
    cropperRef.current?.resetCrop();
    const originalHasBoundingBox = !!image?.boundingBox;
    setHasUserModifiedCrop(originalHasBoundingBox);
    // Mark that user has explicitly reset the crop
    setHasUserResetCrop(true);
  };

  useEffect(() => {
    if (editQueryId && queryId) {
      dispatch(
        updateSelectedQueryModal({
          type: Filters.VISUAL_SIMILARITY,
          id: queryId,
        }),
      );
    }
  }, [editQueryId, queryId, dispatch]);

  return (
    <QueryModal className={styles.modal} modalRef={modalRef}>
      <QueryModalTitle
        icon={
          <div className={styles.titleIcon}>
            <VisualSimilarityModalIcon />
          </div>
        }
      >
        Find Similar
      </QueryModalTitle>
      {image && (
        <>
          <div className={styles.filterContent}>
            <div className={styles.cropperContainer}>
              <VLCropper
                ref={cropperRef}
                src={image.uri}
                boundingBox={image.boundingBox}
                onCropChange={handleCropChange}
                className={styles.cropper}
                autoCrop={autoCrop}
              />
            </div>
            <div className={styles.buttonsContainer}>
              {!hasUserResetCrop &&
              (image.boundingBox || hasUserModifiedCrop) ? (
                <Button
                  type={"link"}
                  icon={<ReloadOutlined />}
                  onClick={handleResetCrop}
                  size={"small"}
                >
                  <>Reset Crop</>
                </Button>
              ) : (
                <Button
                  type={"link"}
                  size={"small"}
                  icon={<CropIcon />}
                  onClick={() => {
                    setAutoCrop(true);
                    setHasUserResetCrop(false);
                  }}
                >
                  <>Crop</>
                </Button>
              )}
              <Button
                type={"primary"}
                size={"small"}
                onClick={handleApply}
                disabled={!hasUserModifiedCrop}
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </QueryModal>
  );
}
